/**
 * Карточка контура `/contours/:id` на каноне (agroai-6al.34): контур —
 * вторая ключевая сущность. Вкладки: «Сейчас» (ситуационная: правила
 * приоритета `priority.ts` — текущая культура и цикл, текущая работа и
 * статус, активные проблемы, затем паспорт и карта контура с соседями),
 * «Паспорт», «Культуры», «Работы», «Карантин», «Вода».
 *
 * `?tab=quarantine` (и прежний якорь `#quarantine`) открывает карантинную
 * историю сразу — так ведут ссылки из реестра карантина и кабинета хозяйства.
 *
 * Откуда что: `/contours/{id}` — паспорт, землепользователи по датам,
 * площади по категориям, хронология культур, карантин (интервалы,
 * обследования, находки); `/contours/{id}/history` — работы по сезонам с
 * состоянием на дату, предписания с повторным контролем, вода контура.
 */

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Droplets, Layers, LayoutDashboard, ListChecks, ShieldAlert, Sprout } from 'lucide-react';
import { api, request } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useServerToday } from '../../../analysis/useSliceDate';
import { useAnalysisContext } from '../../../analysis/context';
import { localIsoDate } from '../../../lib/localDate';
import { cropLabel } from '../../../lib/crops';
import { defineMessages } from '../../../i18n/define';
import { useT, UNIT_LABELS } from '../../../i18n/hooks';
import { COMMON } from '../../../i18n/common';
import { PageHeader } from '../../ui/PageHeader';
import { Card, Grid, Stack } from '../../ui/Card';
import { KpiCard } from '../../ui/KpiCard';
import { EmptyState } from '../../ui/EmptyState';
import { Badge } from '../../ui/Badge';
import { Icon } from '../../ui/Icon';
import { Tabs, TabPanel, useTabParam, type TabItem } from '../../ui/Tabs';
import { Skeleton, SkeletonCard, SkeletonKpi, SkeletonList, SkeletonMap, SkeletonRegion, SkeletonTable } from '../../ui';
import { AgroMap } from '../../map/MapWidget';
import { usePageTrail } from '../../shell/ShellContext';
import { useTrackData } from '../../data/useTrackData';
import { FILES } from '../../data/loader';
import type { Crop } from '../../data/types';
import { dateShort, ha, haUnit, num, windowShort } from '../../lib/format';
import { noData } from '../../data-source/api/compare';
import { contourLandUsers, contourSeasonCrop, contourTotalHa } from '../../data-source/api/contour';
import {
  contourFacts,
  CONTOUR_WORK_STATUS_LABEL,
  cycleName,
  Q_DIRECTION_LABEL,
  Q_STATUS_LABEL,
  REQ_STATE_LABEL,
  workTitle,
  type ContourHistory,
  type ContourWork,
} from './model';
import { arrangeContour, type ContourBlock } from './priority';
import { VoluntaryBlock } from '../voluntary/VoluntaryBlock';
import { contourWaterCycles, ContourQuarantineTab, ContourWaterLines, ContourWorksTab } from './ContourTabs';
import { WateringView } from './WateringView';
import './cabinet.css';

export const CONTOUR_TABS = ['now', 'passport', 'crops', 'works', 'quarantine', 'water'] as const;
export type ContourTab = (typeof CONTOUR_TABS)[number];

type Detail = Awaited<ReturnType<typeof api.contour>>;

const m = defineMessages({
  tabNow: { id: 'contour.cabinet.tab.now', defaultMessage: 'Сейчас', description: 'Вкладка карточки контура: ситуационный обзор (текущая культура, работа, проблемы); коротко' },
  tabPassport: { id: 'contour.cabinet.tab.passport', defaultMessage: 'Паспорт', description: 'Вкладка карточки контура: паспорт участка, землепользователи, площади; коротко' },
  tabCrops: { id: 'contour.cabinet.tab.crops', defaultMessage: 'Культуры', description: 'Вкладка карточки контура: история культур по сезонам; коротко' },
  tabWorks: { id: 'contour.cabinet.tab.works', defaultMessage: 'Работы', description: 'Вкладка карточки контура: работы плана, предписания, добровольные; коротко' },
  tabQuarantine: { id: 'contour.cabinet.tab.quarantine', defaultMessage: 'Карантин', description: 'Вкладка карточки контура: карантинная история и предписания; коротко' },
  tabWater: { id: 'contour.cabinet.tab.water', defaultMessage: 'Вода', description: 'Вкладка карточки контура: поливы контура; коротко' },
  tabsLabel: { id: 'contour.cabinet.tabs.label', defaultMessage: 'Разделы карточки контура', description: 'aria-label списка вкладок карточки контура' },
  tabOverdueBadge: { id: 'contour.cabinet.tab.works.overdueBadgeV2', defaultMessage: '{count} после срока в идущих агроциклах', description: 'Подсказка и подпись для чтения с экрана у числа на вкладке «Работы»: работы после срока в идущих агроциклах (и предписания вне цикла) — основа блока «Сейчас»; вкладка открывается на «Идущие агроциклы»; {count} — число, уже отформатированное' },
  tabQuarantineBadge: { id: 'contour.cabinet.tab.quarantine.statusBadge', defaultMessage: 'статус {status}', description: 'Подсказка у метки на вкладке «Карантин»; {status} — цвет статуса словом («жёлтый», «красный»)' },
  tabWaterBadge: { id: 'contour.cabinet.tab.water.shortBadge', defaultMessage: '{count, plural, one {# позиция} few {# позиции} many {# позиций} other {# позиции}} с недоподачей', description: 'Подсказка у числа на вкладке «Вода»: сколько позиций полива с недоподачей воды' },

  skelPassportRegion: { id: 'contour.cabinet.skeleton.passport.region', defaultMessage: 'паспорт контура', description: 'Что загружается (подпись скелетона для чтения с экрана), после слова «Загружаю»' },
  skelPassportTitle: { id: 'contour.cabinet.skeleton.passport.title', defaultMessage: 'Паспорт контура', description: 'Заголовок карточки-скелетона и карточки паспорта на вкладке «Паспорт»' },
  skelLandUsers: { id: 'contour.cabinet.landUsers.title', defaultMessage: 'Землепользователи по датам', description: 'Заголовок таблицы землепользователей контура на вкладке «Паспорт» (и её скелетона)' },
  skelAreas: { id: 'contour.cabinet.areas.title', defaultMessage: 'Площади по категориям', description: 'Заголовок списка площадей контура по категориям земель (и его скелетона)' },
  mapTitle: { id: 'contour.cabinet.map.title', defaultMessage: 'Контур и соседи на карте', description: 'Заголовок карточки с картой: контур и соседние участки' },
  mapCaption: { id: 'contour.cabinet.map.caption', defaultMessage: 'Контур выделен; вокруг — соседние контуры с их культурами', description: 'Подпись под картой контура на вкладках «Сейчас» и «Паспорт»' },
  skelNowRegion: { id: 'contour.cabinet.skeleton.now.region', defaultMessage: 'контур', description: 'Что загружается (подпись скелетона вкладки «Сейчас» для чтения с экрана), после слова «Загружаю»' },
  nowAside: { id: 'contour.cabinet.now.aside', defaultMessage: 'на {date}', description: 'Пометка справа от заголовка карточки «Сейчас»: дата среза; {date} — короткая дата' },
  nowTitle: { id: 'contour.cabinet.now.title', defaultMessage: 'Сейчас', description: 'Заголовок карточки «Сейчас» на вкладке «Сейчас» карточки контура' },
  skelDataRegion: { id: 'contour.cabinet.skeleton.data.region', defaultMessage: 'данные контура', description: 'Что загружается (подпись скелетона для чтения с экрана), после слова «Загружаю»' },
  skelWaterRegion: { id: 'contour.cabinet.skeleton.water.region', defaultMessage: 'воду контура', description: 'Что загружается (подпись скелетона вкладки «Вода»), после слова «Загружаю»; винительный падеж' },

  trailDistrict: { id: 'contour.cabinet.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком (хлебные крошки): район' },
  trailDistrictName: { id: 'contour.cabinet.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; имя собственное' },
  trailMassif: { id: 'contour.cabinet.trail.massif', defaultMessage: 'Массив', description: 'Уровень в цепочке навигации: массив' },
  trailFarm: { id: 'contour.cabinet.trail.farm', defaultMessage: 'Хозяйство', description: 'Уровень в цепочке навигации: хозяйство' },
  trailContour: { id: 'contour.cabinet.trail.contour', defaultMessage: 'Контур', description: 'Уровень в цепочке навигации: контур' },
  trailContourNo: { id: 'contour.cabinet.trail.contourNo', defaultMessage: '№ {no}', description: 'Контур в цепочке навигации; {no} — кадастровый номер контура' },

  headTitle: { id: 'contour.cabinet.head.title', defaultMessage: 'Контур {id}', description: 'Заголовок страницы, пока контур не загружен или не найден; {id} — адрес контура из ссылки' },
  headTitleNo: { id: 'contour.cabinet.head.titleNo', defaultMessage: 'Контур № {no}', description: 'Заголовок карточки контура; {no} — кадастровый номер' },
  headPurpose: { id: 'contour.cabinet.head.purpose', defaultMessage: 'Массив {massif}. Землепользователь: {users}. Районных и массивных долей здесь нет — у них другой знаменатель.', description: 'Описание под заголовком карточки контура; {massif} — название массива, {users} — землепользователь или сводка по ним' },
  farmCabinet: { id: 'contour.cabinet.head.farmCabinet', defaultMessage: 'Кабинет хозяйства', description: 'Кнопка в шапке карточки контура: перейти в кабинет единственного землепользователя; коротко' },

  notFoundTitle: { id: 'contour.cabinet.error.notFound.title', defaultMessage: 'Контура с таким адресом в срезе нет', description: 'Заголовок пустого состояния: контур по ссылке не найден' },
  outsideTitle: { id: 'contour.cabinet.error.outside.title', defaultMessage: 'Этот контур не относится к вашему хозяйству', description: 'Заголовок пустого состояния для фермера: контур чужого хозяйства' },
  failedTitle: { id: 'contour.cabinet.error.failed.title', defaultMessage: 'Карточка контура не загрузилась', description: 'Заголовок пустого состояния: ошибка загрузки карточки контура' },
  notFoundReason: { id: 'contour.cabinet.error.notFound.reason', defaultMessage: 'В базе района нет контура «{id}». Паспорт, работы, карантин и карта здесь не показываются — показывать про несуществующий контур нечего.', description: 'Пояснение к пустому состоянию «контур не найден»; {id} — адрес из ссылки' },
  outsideReason: { id: 'contour.cabinet.error.outside.reason', defaultMessage: 'Кабинет хозяйства показывает только свои контуры. Это граница области чтения, а не потеря данных.', description: 'Пояснение к пустому состоянию для фермера: чужой контур' },
  failedReason: { id: 'contour.cabinet.error.failed.reason', defaultMessage: 'Сервер ответил ошибкой: {message}.', description: 'Пояснение к ошибке загрузки; {message} — текст ошибки сервера' },
  toRegistry: { id: 'contour.cabinet.error.toRegistry', defaultMessage: 'К реестру контуров', description: 'Кнопка-ссылка в пустом состоянии карточки контура: перейти в реестр контуров' },

  why: { id: 'contour.cabinet.now.why', defaultMessage: 'Почему здесь: {reasons}', description: 'Пояснение под заголовком блока на вкладке «Сейчас», почему блок показан; {reasons} — готовые причины одной строкой' },
  signalDone: { id: 'contour.cabinet.now.signal.done', defaultMessage: 'выполнено', description: 'Кнопка-счётчик на вкладке «Сейчас»: работы с отчётом; после числа («5 · выполнено»); коротко' },
  signalInProgress: { id: 'contour.cabinet.now.signal.inProgress', defaultMessage: 'в процессе', description: 'Кнопка-счётчик на вкладке «Сейчас»: работы в процессе; после числа; коротко' },
  signalWindowOpen: { id: 'contour.cabinet.now.signal.windowOpen', defaultMessage: 'окно идёт', description: 'Кнопка-счётчик на вкладке «Сейчас»: работы с открытым окном, отчёта пока нет; после числа; коротко' },
  signalOverdue: { id: 'contour.cabinet.now.signal.overdue', defaultMessage: 'после срока', description: 'Кнопка-счётчик на вкладке «Сейчас»: окно закрылось, отчёта нет; после числа; коротко' },
  signalAhead: { id: 'contour.cabinet.now.signal.ahead', defaultMessage: 'впереди', description: 'Кнопка-счётчик на вкладке «Сейчас»: работы, у которых окно впереди; после числа; коротко' },
  signalLine: { id: 'contour.cabinet.now.signal.line', defaultMessage: '{count} · {label}', description: 'Кнопка-счётчик: число и подпись; {count} — число, {label} — подпись состояния работ' },
  kpiCrop: { id: 'contour.cabinet.now.kpi.crop', defaultMessage: 'Культура {label}', description: 'Подпись KPI на вкладке «Сейчас»: культура контура; {label} — уточнение источника (может быть пустым)' },
  kpiCropBare: { id: 'contour.cabinet.now.kpi.cropBare', defaultMessage: 'Культура', description: 'Подпись KPI на вкладке «Сейчас», когда уточнения нет' },
  cropNote: { id: 'contour.cabinet.now.kpi.cropNote', defaultMessage: 'текущее размещение', description: 'Мелкая подпись под KPI «Культура»: значение — текущее размещение' },
  cropNoteDemo: { id: 'contour.cabinet.now.kpi.cropNoteDemo', defaultMessage: 'текущее размещение · синтетика', description: 'Мелкая подпись под KPI «Культура»: размещение из демонстрационных (синтетических) данных' },
  cropNoteMissing: { id: 'contour.cabinet.now.kpi.cropNoteMissing', defaultMessage: 'размещения в базе района нет — пробел данных, а не пар', description: 'Мелкая подпись под KPI «Культура», когда размещения нет; «пар» — земля под парами' },
  kpiCycle: { id: 'contour.cabinet.now.kpi.cycle', defaultMessage: 'Агроцикл', description: 'Подпись KPI на вкладке «Сейчас»: агроцикл (культура × сезон) контура' },
  cycleNone: { id: 'contour.cabinet.now.kpi.cycleNone', defaultMessage: 'не идёт', description: 'Значение KPI «Агроцикл», когда на дату ни одно окно работ не открыто' },
  cycleNoteOpen: { id: 'contour.cabinet.now.kpi.cycleNoteOpen', defaultMessage: 'по работам с открытым окном', description: 'Мелкая подпись под KPI «Агроцикл»: агроцикл определён по работам с открытым окном' },
  cycleNoteNone: { id: 'contour.cabinet.now.kpi.cycleNoteNone', defaultMessage: 'окна работ агроциклов на дату не открыты', description: 'Мелкая подпись под KPI «Агроцикл», когда окна работ на дату не открыты' },
  kpiQuarantine: { id: 'contour.cabinet.now.kpi.quarantine', defaultMessage: 'Карантин', description: 'Подпись KPI на вкладке «Сейчас»: карантинный статус контура' },
  quarantineDefault: { id: 'contour.cabinet.now.kpi.quarantineDefault', defaultMessage: 'по умолчанию: событий не было', description: 'Мелкая подпись под KPI «Карантин»: статус зелёный, потому что событий не было' },
  quarantineSince: { id: 'contour.cabinet.now.kpi.quarantineSince', defaultMessage: 'с {date}', description: 'Мелкая подпись под KPI «Карантин»: статус действует с даты; {date} — короткая дата' },
  quarantineOpen: { id: 'contour.cabinet.now.kpi.quarantineOpen', defaultMessage: 'Открыть карантин контура', description: 'aria-label карточки KPI «Карантин»: открыть вкладку карантина' },
  runningTitle: { id: 'contour.cabinet.now.running.title', defaultMessage: 'Работы контура в идущих агроциклах', description: 'Подзаголовок над кнопками-счётчиками на вкладке «Сейчас»: работы контура по состояниям в идущих агроциклах' },
  currentTitle: { id: 'contour.cabinet.now.current.title', defaultMessage: 'Текущая работа', description: 'Подзаголовок списка работ с открытым окном на вкладке «Сейчас»' },
  currentSkelRegion: { id: 'contour.cabinet.now.current.skeletonRegion', defaultMessage: 'работы контура', description: 'Что загружается (подпись скелетона для чтения с экрана), после слова «Загружаю»' },
  noCurrent: { id: 'contour.cabinet.now.current.none', defaultMessage: 'На {date} окно ни одной работы контура не открыто.', description: 'Пустое состояние списка текущих работ; {date} — дата среза' },
  noCurrentNext: { id: 'contour.cabinet.now.current.noneNext', defaultMessage: 'На {date} окно ни одной работы контура не открыто; следующая — «{title}» с {from}.', description: 'Пустое состояние списка текущих работ со следующей работой; {date} — дата среза, {title} — название работы, {from} — дата начала окна' },
  currentMeta: { id: 'contour.cabinet.now.current.meta', defaultMessage: 'окно {window} · {status}', description: 'Мелкая строка у работы в списке текущих; {window} — окно дат, {status} — состояние работы' },
  currentMetaReported: { id: 'contour.cabinet.now.current.metaReported', defaultMessage: 'окно {window} · {status}, отчёт {date}', description: 'Мелкая строка у работы в списке текущих, отчёт подан; {window} — окно дат, {status} — состояние, {date} — дата отчёта' },

  alertQuarantineTitle: { id: 'contour.cabinet.alert.quarantine.title', defaultMessage: 'Карантин требует внимания', description: 'Заголовок блока на вкладке «Сейчас»: открытые предписания по карантину' },
  alertQuarantineAll: { id: 'contour.cabinet.alert.quarantine.all', defaultMessage: 'Вся история', description: 'Кнопка в блоке карантина на вкладке «Сейчас»: открыть вкладку карантина; коротко' },
  problemUnnamed: { id: 'contour.cabinet.alert.quarantine.problemUnnamed', defaultMessage: 'проблема не названа', description: 'Подстановка вместо названия проблемы (вредитель, болезнь, сорняк), когда оно не задано' },
  alertFinding: { id: 'contour.cabinet.alert.quarantine.finding', defaultMessage: '{direction}: {problem}', description: 'Строка предписания: {direction} — направление («вредители», «болезни», «сорняки»), {problem} — название проблемы' },
  alertDeadline: { id: 'contour.cabinet.alert.quarantine.deadline', defaultMessage: '{state} · срок {date}', description: 'Мелкая строка предписания; {state} — состояние предписания, {date} — срок устранения' },
  alertNoDeadline: { id: 'contour.cabinet.alert.quarantine.noDeadline', defaultMessage: '{state} · срок не назначен', description: 'Мелкая строка предписания без срока; {state} — состояние предписания' },
  alertWorksTitle: { id: 'contour.cabinet.alert.works.title', defaultMessage: 'Работы после срока', description: 'Заголовок блока на вкладке «Сейчас»: работы, окно которых закрылось без отчёта' },
  alertWorksAll: { id: 'contour.cabinet.alert.works.all', defaultMessage: 'Все работы', description: 'Кнопка в блоке работ после срока: открыть вкладку «Работы»; коротко' },
  alertWorksMeta: { id: 'contour.cabinet.alert.works.meta', defaultMessage: 'окно закрылось {date} · без отчёта', description: 'Мелкая строка у работы после срока; {date} — дата закрытия окна' },
  alertWorksNote: { id: 'contour.cabinet.alert.works.note', defaultMessage: '«После срока» — отчёта нет при закрытом окне; это про данные, а не про работу в поле.', description: 'Пояснение под списком работ после срока: статус говорит об отсутствии отчёта, а не о невыполнении работы' },
  alertWaterTitle: { id: 'contour.cabinet.alert.water.title', defaultMessage: 'Вода: недоподача', description: 'Заголовок блока на вкладке «Сейчас»: недоподача воды' },
  alertWaterAll: { id: 'contour.cabinet.alert.water.all', defaultMessage: 'Все поливы', description: 'Кнопка в блоке воды: открыть вкладку «Вода»; коротко' },
  allClear: { id: 'contour.cabinet.allClear', defaultMessage: 'Активных проблем нет на {date}: карантин зелёный, работ после срока и недоподачи нет.', description: 'Сообщение вместо блоков проблем на вкладке «Сейчас»; {date} — дата среза' },

  passTitle: { id: 'contour.cabinet.passportBlock.title', defaultMessage: 'Паспорт', description: 'Заголовок карточки-сводки паспорта на вкладке «Сейчас»' },
  passMore: { id: 'contour.cabinet.passportBlock.more', defaultMessage: 'Подробно', description: 'Кнопка в карточке-сводке паспорта: открыть вкладку «Паспорт»; коротко' },
  kpiArea: { id: 'contour.cabinet.passport.kpi.area', defaultMessage: 'Площадь', description: 'Подпись KPI: площадь контура в гектарах земли' },
  kpiAreaNoteRegistry: { id: 'contour.cabinet.passport.kpi.areaNoteRegistry', defaultMessage: 'по реестру района', description: 'Мелкая подпись под KPI «Площадь» в сводке на вкладке «Сейчас»' },
  kpiAreaNoteTotal: { id: 'contour.cabinet.passport.kpi.areaNoteTotal', defaultMessage: 'общая площадь по реестру', description: 'Мелкая подпись под KPI «Площадь» на вкладке «Паспорт»' },
  kpiArable: { id: 'contour.cabinet.passport.kpi.arable', defaultMessage: 'Пашня', description: 'Подпись KPI: пашня контура, га земли' },
  arableNoteIrrigated: { id: 'contour.cabinet.passport.kpi.arableNoteIrrigated', defaultMessage: 'орошаемая {area}', description: 'Мелкая подпись под KPI «Пашня»; {area} — площадь орошаемой пашни с единицей («12,5 га»)' },
  arableNoteNone: { id: 'contour.cabinet.passport.kpi.arableNoteNone', defaultMessage: 'орошаемой пашни в реестре нет', description: 'Мелкая подпись под KPI «Пашня», когда орошаемой пашни в реестре нет' },
  arableNoteTab: { id: 'contour.cabinet.passport.kpi.arableNoteTab', defaultMessage: 'пахотные земли', description: 'Мелкая подпись под KPI «Пашня» на вкладке «Паспорт»' },
  kpiLandUsers: { id: 'contour.cabinet.passport.kpi.landUsers', defaultMessage: 'Землепользователей', description: 'Подпись KPI: сколько землепользователей у контура' },
  landUsersNote: { id: 'contour.cabinet.passport.kpi.landUsersNote', defaultMessage: 'записей в истории: {count}', description: 'Мелкая подпись под KPI «Землепользователей»; {count} — число записей истории' },
  kpiIrrigated: { id: 'contour.cabinet.passport.kpi.irrigated', defaultMessage: 'Орошаемая', description: 'Подпись KPI: орошаемая пашня контура, га земли' },
  irrigatedNote: { id: 'contour.cabinet.passport.kpi.irrigatedNote', defaultMessage: 'богара — {area}', description: 'Мелкая подпись под KPI «Орошаемая»; {area} — площадь богары (неорошаемой пашни) с единицей, либо «нет данных»' },
  kpiMassif: { id: 'contour.cabinet.passport.kpi.massif', defaultMessage: 'Массив', description: 'Подпись KPI: массив, в котором лежит контур' },
  massifNote: { id: 'contour.cabinet.passport.kpi.massifNote', defaultMessage: 'территориальный фильтр, не единица учёта', description: 'Мелкая подпись под KPI «Массив»: массив — только территориальный фильтр' },
  recordsCount: { id: 'contour.cabinet.landUsers.count', defaultMessage: '{count, plural, one {# запись} few {# записи} many {# записей} other {# записи}}', description: 'Число записей истории землепользования справа от заголовка таблицы' },
  colFarm: { id: 'contour.cabinet.landUsers.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы землепользователей: хозяйство' },
  colPart: { id: 'contour.cabinet.landUsers.col.part', defaultMessage: 'Часть', description: 'Колонка таблицы землепользователей: часть контура; узкая колонка' },
  colFrom: { id: 'contour.cabinet.landUsers.col.from', defaultMessage: 'С', description: 'Колонка таблицы землепользователей: дата начала; предельно коротко' },
  colTo: { id: 'contour.cabinet.landUsers.col.to', defaultMessage: 'По', description: 'Колонка таблицы землепользователей: дата окончания; предельно коротко' },
  colNow: { id: 'contour.cabinet.landUsers.col.now', defaultMessage: 'Сейчас', description: 'Колонка таблицы землепользователей: действует ли запись сейчас; узкая колонка' },
  subjectFallback: { id: 'contour.cabinet.landUsers.subjectFallback', defaultMessage: 'субъект {id}', description: 'Подстановка вместо названия хозяйства, когда его нет; {id} — числовой код субъекта' },
  wholeContour: { id: 'contour.cabinet.landUsers.wholeContour', defaultMessage: 'весь контур', description: 'Ячейка «Часть» таблицы землепользователей: хозяйство держит весь контур, а не его часть' },
  startUndated: { id: 'contour.cabinet.landUsers.startUndated', defaultMessage: 'начало не датировано', description: 'Ячейка «С» таблицы землепользователей: дата начала в базе не задана' },
  endOpen: { id: 'contour.cabinet.landUsers.endOpen', defaultMessage: 'по сей день', description: 'Ячейка «По» таблицы землепользователей: запись не закрыта' },
  badgeActive: { id: 'contour.cabinet.landUsers.badge.active', defaultMessage: 'действует', description: 'Бейдж в таблице землепользователей: запись действует; коротко' },
  badgePast: { id: 'contour.cabinet.landUsers.badge.past', defaultMessage: 'в прошлом', description: 'Бейдж в таблице землепользователей: запись закончилась; коротко' },
  landUsersNote2: { id: 'contour.cabinet.landUsers.note', defaultMessage: 'Открытый конец — окончание не зафиксировано, а не «бессрочно». Начало без даты — в базе района оно не датировано.', description: 'Пояснение под таблицей землепользователей' },
  categoriesCount: { id: 'contour.cabinet.areas.count', defaultMessage: '{count, plural, one {# категория} few {# категории} many {# категорий} other {# категории}}', description: 'Число категорий земель справа от заголовка списка площадей' },
  areaNoValue: { id: 'contour.cabinet.areas.noValue', defaultMessage: '{area} · без величины {missing} из {rows}', description: 'Мелкая строка категории площади, когда у части записей нет величины; {area} — площадь с единицей, {missing} — сколько записей без величины, {rows} — сколько всего' },

  cropsTitle: { id: 'contour.cabinet.crops.title', defaultMessage: 'История культур по сезонам', description: 'Заголовок карточки на вкладке «Культуры» карточки контура' },
  yearsCount: { id: 'contour.cabinet.crops.yearsCount', defaultMessage: '{count, plural, one {# год} few {# года} many {# лет} other {# года}}', description: 'Число лет в истории культур справа от заголовка' },
  cropsEmptyTitle: { id: 'contour.cabinet.crops.empty.title', defaultMessage: 'Культур нет', description: 'Заголовок пустого состояния вкладки «Культуры»' },
  cropsEmptyReason: { id: 'contour.cabinet.crops.empty.reason', defaultMessage: 'размещений культур на контуре в срезе нет — это пробел данных, а не пар', description: 'Пояснение пустого состояния вкладки «Культуры»; «пар» — земля под парами' },
  cropsYear: { id: 'contour.cabinet.crops.year', defaultMessage: 'Агроцикл {year} года', description: 'Подзаголовок блока истории культур; {year} — год агроцикла' },
  cropsDemo: { id: 'contour.cabinet.crops.demo', defaultMessage: 'синтетика демонстрации', description: 'Пометка у года истории культур: данные демонстрационные (синтетические)' },
  cropUnnamed: { id: 'contour.cabinet.crops.cropUnnamed', defaultMessage: 'культура не названа', description: 'Бейдж культуры в истории, когда название не задано' },
  cropPart: { id: 'contour.cabinet.crops.part', defaultMessage: ' · часть {part}', description: 'Часть хвоста строки культуры: размещение на части контура; {part} — подпись части; начинается с « · »' },
  cropCurrent: { id: 'contour.cabinet.crops.current', defaultMessage: ' · сейчас', description: 'Часть хвоста строки культуры: размещение действует сейчас; начинается с « · »' },
  cropMeta: { id: 'contour.cabinet.crops.meta', defaultMessage: '{area} · план {window}', description: 'Мелкая строка размещения культуры; {area} — площадь с единицей, {window} — плановое окно дат' },
  cropMetaFact: { id: 'contour.cabinet.crops.metaFact', defaultMessage: '{area} · план {window} · факт с {from}', description: 'Мелкая строка размещения культуры с фактом; {area} — площадь с единицей, {window} — плановое окно, {from} — дата фактического начала' },
  phaseCotton: { id: 'contour.cabinet.crops.phase.cotton', defaultMessage: 'хлопок', description: 'Фаза размещения культуры в истории: хлопковая' },
  phaseWinterGrain: { id: 'contour.cabinet.crops.phase.winterGrain', defaultMessage: 'озимые', description: 'Фаза размещения культуры в истории: озимые зерновые' },
  phaseSecondary: { id: 'contour.cabinet.crops.phase.secondary', defaultMessage: 'повторная/второстепенная', description: 'Фаза размещения культуры в истории: повторная или второстепенная культура' },
});

const PHASE_MSG = { cotton: m.phaseCotton, winter_grain: m.phaseWinterGrain, secondary: m.phaseSecondary } as const;


/** Вкладки без значков: пока контур не пришёл, счётчиков ещё нет, а сами разделы уже известны. */
const contourTabShell = (t: ReturnType<typeof useT>): TabItem<ContourTab>[] => [
  { id: 'now', label: t(m.tabNow), icon: LayoutDashboard },
  { id: 'passport', label: t(m.tabPassport), icon: Layers },
  { id: 'crops', label: t(m.tabCrops), icon: Sprout },
  { id: 'works', label: t(m.tabWorks), icon: ListChecks },
  { id: 'quarantine', label: t(m.tabQuarantine), icon: ShieldAlert },
  { id: 'water', label: t(m.tabWater), icon: Droplets },
];

/** Скелетон тела карточки: раскладка выбранной вкладки, блоки в своих размерах. */
function ContourBodySkeleton({ tab }: { tab: ContourTab }) {
  const t = useT();
  if (tab === 'passport') {
    return (
      <SkeletonRegion label={t(m.skelPassportRegion)}>
        <Stack>
          <Card size="lg" title={t(m.skelPassportTitle)}>
            <Grid cols={4}>{Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} quiet />)}</Grid>
          </Card>
          <SkeletonTable quiet title={t(m.skelLandUsers)} rows={3} cols={5} />
          <SkeletonCard quiet title={t(m.skelAreas)} height={120}><SkeletonList quiet items={3} tone="soft" /></SkeletonCard>
          <SkeletonCard quiet size="lg" title={t(m.mapTitle)}><SkeletonMap quiet height={360} /></SkeletonCard>
        </Stack>
      </SkeletonRegion>
    );
  }
  if (tab === 'now') {
    return (
      <SkeletonRegion label={t(m.skelNowRegion)}>
        <Stack>
          <Card size="lg" title={t(m.nowTitle)}>
            <div className="contour-now-skel"><Grid cols={3}>{Array.from({ length: 3 }, (_, i) => <SkeletonKpi key={i} quiet />)}</Grid></div>
            <div style={{ marginTop: 'var(--sp-3)', display: 'grid', gap: 'var(--sp-2)' }}>
              <Skeleton width={220} className="skel--title" />
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                {[96, 96, 92, 112, 84].map((w, i) => <Skeleton key={i} variant="block" width={w} height={28} className="skel--control" />)}
              </div>
              <Skeleton width={150} className="skel--title" />
              <SkeletonList quiet items={3} tone="soft" />
            </div>
          </Card>
          <SkeletonCard quiet size="lg" title={t(m.mapTitle)}><SkeletonMap quiet height={360} /></SkeletonCard>
        </Stack>
      </SkeletonRegion>
    );
  }
  return (
    <SkeletonRegion label={t(m.skelDataRegion)}>
      <SkeletonCard quiet size="lg" height={240}><SkeletonList quiet items={4} tone="soft" /></SkeletonCard>
    </SkeletonRegion>
  );
}


export function ContourCabinet() {
  const t = useT();
  const { id = '' } = useParams();
  const numericId = Number(id);
  const valid = Number.isInteger(numericId) && numericId > 0;
  const ctx = useAnalysisContext();
  const [tab, setTab] = useTabParam<ContourTab>(CONTOUR_TABS, 'now');

  const detail = useRequest(() => (valid ? api.contour(numericId, ctx.params) : Promise.reject(new Error('bad id'))), [numericId, ctx.key]);
  const history = useRequest(
    () => (valid ? request<ContourHistory>(`/contours/${numericId}/history`, ctx.params) : Promise.reject(new Error('bad id'))),
    [numericId, ctx.key],
  );
  // «Сегодня» и срез — числа СЕРВЕРА (`horizon`), не часы браузера; часы вкладки —
  // страховка на миг, пока сервер не ответил.
  const serverNow = useServerToday();
  const today = serverNow ?? (ctx.asOf ? null : history.data?.horizon ?? null) ?? localIsoDate();
  const asOf = ctx.asOf ?? today;
  const contour = detail.data;
  const landUsers = contour ? contourLandUsers(contour) : null;
  const soleFarm = landUsers?.count === 1 ? landUsers.users[0]! : null;

  usePageTrail([
    { level: t(m.trailDistrict), name: t(m.trailDistrictName), to: '/' },
    { level: t(m.trailMassif), name: contour?.massif.canonical_name ?? '—', to: contour ? `/contours?massif=${contour.massif.id}` : undefined },
    {
      level: t(m.trailFarm),
      name: soleFarm ? soleFarm.name : landUsers ? landUsers.headline : '—',
      to: soleFarm ? `/farms/${soleFarm.id}${ctx.linkSearch()}` : undefined,
    },
    { level: t(m.trailContour), name: contour ? t(m.trailContourNo, { no: contour.contour_no }) : id },
  ]);

  const facts = useMemo(
    () => contourFacts({ asOf, today, quarantineStatus: contour?.quarantine.status ?? null, history: history.data }),
    [asOf, today, contour, history.data],
  );
  const blocks = useMemo(() => arrangeContour(facts), [facts]);
  const water = useMemo(() => contourWaterCycles(history.data, asOf), [history.data, asOf]);

  if (!valid || (detail.error && !detail.data)) {
    const notFound = !valid || detail.error?.status === 404;
    const outside = detail.error?.code === 'outside_scope';
    return (
      <Stack>
        <PageHeader level="contour" title={t(m.headTitle, { id })} />
        <EmptyState
          title={notFound ? t(m.notFoundTitle) : outside ? t(m.outsideTitle) : t(m.failedTitle)}
          reason={
            notFound
              ? t(m.notFoundReason, { id })
              : outside
                ? t(m.outsideReason)
                : t(m.failedReason, { message: detail.error?.message ?? t(COMMON.noReason) })
          }
          action={<Link className="btn btn--secondary focus-ring" to="/contours">{t(m.toRegistry)}</Link>}
        />
      </Stack>
    );
  }
  if (!contour || !landUsers) {
    // Заголовок и вкладки известны до данных: стоят сразу, меняется только тело (6al.49).
    return (
      <Stack>
        <PageHeader
          level="contour"
          title={t(m.headTitle, { id })}
          actions={<Skeleton variant="block" width={146} height={38} className="skel--control" />}
        >
          {/* Строка «массив · землепользователь» — две строки описания готовой шапки. */}
          <div className="pagehead__purpose" aria-hidden="true" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
            <Skeleton width="min(630px, 100%)" />
            <Skeleton width="min(310px, 100%)" />
          </div>
        </PageHeader>
        <div data-guide="tabs">
          <Tabs label={t(m.tabsLabel)} items={contourTabShell(t)} value={tab} onChange={setTab} idPrefix="contour-tab" />
        </div>
        {/* Отступ панели вкладки (`atabs__panel`), как у готового тела. */}
        <div className="atabs__panel"><ContourBodySkeleton tab={tab} /></div>
      </Stack>
    );
  }

  const q = contour.quarantine;
  const items: TabItem<ContourTab>[] = [
    { id: 'now', label: t(m.tabNow), icon: LayoutDashboard },
    { id: 'passport', label: t(m.tabPassport), icon: Layers },
    { id: 'crops', label: t(m.tabCrops), icon: Sprout, badge: contour.crop_timeline.length ? num(contour.crop_timeline.length) : undefined, tone: 'quiet' },
    { id: 'works', label: t(m.tabWorks), icon: ListChecks, badge: facts.overdueWorks ? num(facts.overdueWorks) : undefined, tone: 'warning', badgeLabel: facts.overdueWorks ? t(m.tabOverdueBadge, { count: num(facts.overdueWorks) }) : undefined },
    { id: 'quarantine', label: t(m.tabQuarantine), icon: ShieldAlert, badge: q.status !== 'green' ? Q_STATUS_LABEL[q.status] : undefined, tone: q.status === 'red' ? 'critical' : 'warning', badgeLabel: q.status !== 'green' ? t(m.tabQuarantineBadge, { status: Q_STATUS_LABEL[q.status] ?? q.status }) : undefined },
    { id: 'water', label: t(m.tabWater), icon: Droplets, badge: facts.waterShort ? num(facts.waterShort) : undefined, tone: 'warning', badgeLabel: facts.waterShort ? t(m.tabWaterBadge, { count: facts.waterShort }) : undefined },
  ];

  return (
    <Stack>
      <PageHeader
        level="contour"
        title={t(m.headTitleNo, { no: contour.contour_no })}
        purpose={t(m.headPurpose, { massif: contour.massif.canonical_name, users: landUsers.headline })}
        actions={
          soleFarm ? (
            <Link className="astra-button" to={`/farms/${soleFarm.id}${ctx.linkSearch()}`}>
              {t(m.farmCabinet)}
            </Link>
          ) : null
        }
      />
      {/* Общий контур под областью хозяйства: чужие части не показаны, итоги — по своим (подпись сервера). */}
      {contour.scope_note ? <p className="t-caption" role="note">{contour.scope_note}</p> : null}
      <div data-guide="tabs">
        <Tabs label={t(m.tabsLabel)} items={items} value={tab} onChange={setTab} idPrefix="contour-tab" />
      </div>
      <TabPanel id="now" value={tab} idPrefix="contour-tab">
        <Stack>
          {blocks.map((b) => (
            <NowSection key={b.block} block={b.block} reasons={b.reasons} contour={contour} history={history} asOf={asOf} openTab={setTab} />
          ))}
        </Stack>
      </TabPanel>
      <TabPanel id="passport" value={tab} idPrefix="contour-tab">
        <Passport contour={contour} />
      </TabPanel>
      <TabPanel id="crops" value={tab} idPrefix="contour-tab">
        <CropsTab contour={contour} />
      </TabPanel>
      <TabPanel id="works" value={tab} idPrefix="contour-tab">
        <Stack>
          <ContourWorksTab history={history} asOf={asOf} ctx={ctx} />
          <VoluntaryBlock scope={{ kind: 'contour', contourId: numericId, actorId: soleFarm ? Number(soleFarm.id) : undefined }} />
        </Stack>
      </TabPanel>
      <TabPanel id="quarantine" value={tab} idPrefix="contour-tab">
        <ContourQuarantineTab contour={contour} history={history.data} asOf={asOf} ctx={ctx} onChanged={() => { detail.reload(); history.reload(); }} />
      </TabPanel>
      <TabPanel id="water" value={tab} idPrefix="contour-tab">
        {history.data ? (
          <div data-guide="water">
            <WateringView
              scope={{ kind: 'contour', id: contour.id }}
              cycles={water.cycles}
              defaultCycle={water.fallback}
              asOf={asOf}
              params={ctx.params}
              paramsKey={ctx.key}
              linkSearch={ctx.linkSearch}
              after={<ContourWaterLines history={history.data} />}
            />
          </div>
        ) : (
          <SkeletonCard label={t(m.skelWaterRegion)} size="lg" height={240} />
        )}
      </TabPanel>
    </Stack>
  );
}

type HistoryReq = ReturnType<typeof useRequest<ContourHistory>>;

/* ------------------------------------------------------------- Сейчас */

function NowSection({
  block,
  reasons,
  contour,
  history,
  asOf,
  openTab,
}: {
  block: ContourBlock;
  reasons: string[];
  contour: Detail;
  history: HistoryReq;
  asOf: string;
  openTab: (t: ContourTab) => void;
}) {
  const t = useT();
  const why = <p className="cab-why">{t(m.why, { reasons: reasons.join(' ') })}</p>;
  const works = history.data?.works ?? [];
  switch (block) {
    case 'now': {
      const crop = contourSeasonCrop(contour);
      // Работы идущих циклов контура тремя исходами и после срока (журнал №290):
      // «идущий» — статус агроцикла с сервера, а не открытое сегодня окно
      // (agroai-0e8: окно цикла 2025 не делает его идущим).
      const running = works.filter((w) => w.cycle_running);
      const current = running.filter((w) => (!w.window_from || w.window_from <= asOf) && (!w.window_to || w.window_to >= asOf));
      const cycles = [...new Set(running.map((w) => w.cycle).filter(Boolean))];
      const next = running.filter((w) => w.status === 'awaiting_window').sort((a, b) => (a.window_from ?? '').localeCompare(b.window_from ?? ''))[0];
      const by = (st: string) => running.filter((w) => w.status === st).length;
      const signals = [
        { label: t(m.signalDone), value: by('reported'), tone: '' },
        { label: t(m.signalInProgress), value: by('in_progress'), tone: '' },
        { label: t(m.signalWindowOpen), value: by('in_window'), tone: '' },
        { label: t(m.signalOverdue), value: by('overdue'), tone: by('overdue') ? ' cab-signal--warn' : '' },
        { label: t(m.signalAhead), value: by('awaiting_window'), tone: '' },
      ];
      return (
        <div data-guide="now">
          <Card size="lg" title={t(m.nowTitle)} aside={t(m.nowAside, { date: dateShort(asOf) })}>
            <Grid cols={3}>
              <div className="kpi-text"><KpiCard label={crop.label ? t(m.kpiCrop, { label: crop.label }) : t(m.kpiCropBare)} value={crop.value ?? noData()} note={crop.value ? (crop.demo ? t(m.cropNoteDemo) : t(m.cropNote)) : t(m.cropNoteMissing)} /></div>
              <div className="kpi-text"><KpiCard label={t(m.kpiCycle)} value={cycles.length ? cycles.map((c) => cycleName({ crop_code: c!.split(':')[1]!, season_label: c!.split(':')[0]! })).join(', ') : t(m.cycleNone)} note={cycles.length ? t(m.cycleNoteOpen) : t(m.cycleNoteNone)} /></div>
              <div className="kpi-text"><KpiCard label={t(m.kpiQuarantine)} value={Q_STATUS_LABEL[contour.quarantine.status] ?? contour.quarantine.status} note={contour.quarantine.is_default_green ? t(m.quarantineDefault) : t(m.quarantineSince, { date: dateShort(contour.quarantine.effective_on) })} onClick={() => openTab('quarantine')} actionLabel={t(m.quarantineOpen)} /></div>
            </Grid>
            {running.length ? (
              <div style={{ marginTop: 'var(--sp-3)' }}>
                <strong>{t(m.runningTitle)}</strong>
                <div className="cab-signals">
                  {signals.map((x) => (
                    <button key={x.label} type="button" className={`cab-signal${x.tone}`} onClick={() => openTab('works')}>
                      {t(m.signalLine, { count: num(x.value), label: x.label })}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <strong>{t(m.currentTitle)}</strong>
              {history.state === 'loading' ? (
                <SkeletonList label={t(m.currentSkelRegion)} items={2} tone="soft" />
              ) : current.length === 0 ? (
                <p className="cab-why">
                  {next
                    ? t(m.noCurrentNext, { date: dateShort(asOf), title: workTitle(next.campaign_title), from: dateShort(next.window_from) })
                    : t(m.noCurrent, { date: dateShort(asOf) })}
                </p>
              ) : (
                <ul className="cab-list">
                  {current.slice(0, 4).map((w) => (
                    <li className="cab-list__row" key={w.obligation_id}>
                      <WorkLink w={w} />
                      <span className="cab-list__meta">
                        {w.reported_on
                          ? t(m.currentMetaReported, { window: windowShort(w.window_from, w.window_to), status: CONTOUR_WORK_STATUS_LABEL[w.status] ?? w.status, date: dateShort(w.reported_on) })
                          : t(m.currentMeta, { window: windowShort(w.window_from, w.window_to), status: CONTOUR_WORK_STATUS_LABEL[w.status] ?? w.status })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      );
    }
    case 'alert-quarantine':
      return (
        <div data-guide="attention-quarantine">
          <Card title={t(m.alertQuarantineTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('quarantine')}>{t(m.alertQuarantineAll)}</button>}>
            {why}
            <ul className="cab-list">
              {(history.data?.requirements ?? []).filter((r) => r.requirement.state === 'open' || r.requirement.state === 'overdue').map((r) => (
                <li className="cab-list__row" key={r.requirement.id}>
                  <span>{t(m.alertFinding, { direction: Q_DIRECTION_LABEL[r.finding.direction] ?? r.finding.direction, problem: r.finding.problem_name ?? t(m.problemUnnamed) })}</span>
                  <span className="cab-list__meta">
                    {r.requirement.deadline
                      ? t(m.alertDeadline, { state: REQ_STATE_LABEL[r.requirement.state] ?? r.requirement.state, date: dateShort(r.requirement.deadline) })
                      : t(m.alertNoDeadline, { state: REQ_STATE_LABEL[r.requirement.state] ?? r.requirement.state })}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      );
    case 'alert-works':
      return (
        <div data-guide="attention-works">
          <Card title={t(m.alertWorksTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('works')}>{t(m.alertWorksAll)}</button>}>
            {why}
            <ul className="cab-list">
              {works.filter((w) => w.cycle_running && w.status === 'overdue').slice(0, 5).map((w) => (
                <li className="cab-list__row" key={w.obligation_id}>
                  <WorkLink w={w} />
                  <span className="cab-list__meta">{t(m.alertWorksMeta, { date: dateShort(w.window_to) })}</span>
                </li>
              ))}
            </ul>
            <p className="cab-why">{t(m.alertWorksNote)}</p>
          </Card>
        </div>
      );
    case 'alert-water':
      return (
        <div data-guide="attention-water">
          <Card title={t(m.alertWaterTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('water')}>{t(m.alertWaterAll)}</button>}>
            {why}
          </Card>
        </div>
      );
    case 'all-clear':
      return (
        <div data-guide="all-clear" className="cab-allclear" role="status">
          <Icon icon={CheckCircle2} size={18} />
          <span>{t(m.allClear, { date: dateShort(asOf) })}</span>
        </div>
      );
    case 'passport': {
      const total = contourTotalHa(contour);
      const arable = contour.totals.find((t) => t.code === 'ARABLE')?.area_microha ?? null;
      const irrigated = contour.totals.find((t) => t.code === 'ARABLE_IRRIGATED')?.area_microha ?? null;
      return (
        <div data-guide="passport-summary">
          <Card title={t(m.passTitle)} aside={<button type="button" className="cab-signal" onClick={() => openTab('passport')}>{t(m.passMore)}</button>}>
            <Grid cols={3}>
              <KpiCard label={t(m.kpiArea)} value={total === null ? noData() : ha(total)} unit={total === null ? undefined : t(UNIT_LABELS.ha)} note={t(m.kpiAreaNoteRegistry)} />
              <KpiCard label={t(m.kpiArable)} value={arable === null ? noData() : ha(arable / 1e6)} unit={arable === null ? undefined : t(UNIT_LABELS.ha)} note={irrigated ? t(m.arableNoteIrrigated, { area: haUnit(irrigated / 1e6) }) : t(m.arableNoteNone)} />
              <KpiCard label={t(m.kpiLandUsers)} value={num(contour.land_uses.length)} note={t(m.landUsersNote, { count: num(contour.land_use_history.length) })} />
            </Grid>
          </Card>
        </div>
      );
    }
    case 'map':
      return <ContourMap contour={contour} />;
    default:
      return null;
  }
}

function WorkLink({ w }: { w: ContourWork }) {
  const ctx = useAnalysisContext();
  if (!w.cycle) return <span>{workTitle(w.campaign_title)}</span>;
  return <Link to={`/cycles/${encodeURIComponent(w.cycle)}/works/${w.campaign_id}${ctx.linkSearch({ farm: w.actor.id, cycle: w.cycle })}`}>{workTitle(w.campaign_title)}</Link>;
}

function ContourMap({ contour }: { contour: Detail }) {
  const t = useT();
  const crops = useTrackData<Crop[]>(FILES.crops);
  const id = String(contour.id);
  return (
    <div data-guide="map">
      <Card size="lg" title={t(m.mapTitle)}>
        <AgroMap colorBy="crop" crops={crops.data ?? undefined} focusIds={[id]} selectedId={id} mode="frame" height={360} legend="collapsed" caption={t(m.mapCaption)} />
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------- паспорт */

function Passport({ contour }: { contour: Detail }) {
  const t = useT();
  const ctx = useAnalysisContext();
  const total = contourTotalHa(contour);
  const byCode = new Map(contour.totals.map((t) => [t.code, t]));
  const haOf = (code: string) => {
    const v = byCode.get(code)?.area_microha;
    return v === null || v === undefined ? null : v / 1e6;
  };
  const historyRows = [...contour.land_use_history].sort((a, b) => (b.valid_from ?? '').localeCompare(a.valid_from ?? ''));
  return (
    <Stack>
      <div data-guide="passport">
        <Card size="lg" title={t(m.skelPassportTitle)}>
          <Grid cols={4}>
            <KpiCard label={t(m.kpiArea)} value={total === null ? noData() : ha(total)} unit={total === null ? undefined : t(UNIT_LABELS.ha)} note={t(m.kpiAreaNoteTotal)} />
            <KpiCard label={t(m.kpiArable)} value={haOf('ARABLE') === null ? noData() : ha(haOf('ARABLE'))} unit={t(UNIT_LABELS.ha)} note={t(m.arableNoteTab)} />
            <KpiCard label={t(m.kpiIrrigated)} value={haOf('ARABLE_IRRIGATED') === null ? noData() : ha(haOf('ARABLE_IRRIGATED'))} unit={t(UNIT_LABELS.ha)} note={t(m.irrigatedNote, { area: haOf('ARABLE_RAINFED') === null ? noData() : haUnit(haOf('ARABLE_RAINFED')) })} />
            <div className="kpi-text"><KpiCard label={t(m.kpiMassif)} value={contour.massif.canonical_name} note={t(m.massifNote)} to={`/contours?massif=${contour.massif.id}`} /></div>
          </Grid>
        </Card>
      </div>
      <div data-guide="land-users">
        <Card title={t(m.skelLandUsers)} aside={t(m.recordsCount, { count: historyRows.length })}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t(m.colFarm)}</th>
                <th scope="col">{t(m.colPart)}</th>
                <th scope="col">{t(m.colFrom)}</th>
                <th scope="col">{t(m.colTo)}</th>
                <th scope="col">{t(m.colNow)}</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((u) => (
                <tr key={u.id}>
                  <td><Link to={`/farms/${u.actor.id}${ctx.linkSearch()}`}>{u.actor.name ?? t(m.subjectFallback, { id: u.actor.id })}</Link></td>
                  <td>{u.part_label ?? t(m.wholeContour)}</td>
                  <td>{u.valid_from ? dateShort(u.valid_from) : t(m.startUndated)}</td>
                  <td>{u.valid_to ? dateShort(u.valid_to) : t(m.endOpen)}</td>
                  <td>{u.is_current ? <Badge tone="sage">{t(m.badgeActive)}</Badge> : <Badge tone="outline">{t(m.badgePast)}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cab-why">{t(m.landUsersNote2)}</p>
        </Card>
      </div>
      <div data-guide="areas">
        <Card title={t(m.skelAreas)} aside={t(m.categoriesCount, { count: contour.totals.length })}>
          <ul className="cab-list">
            {contour.totals.filter((row) => row.area_microha !== 0).map((row) => (
              <li className="cab-list__row" key={row.code}>
                <span>{row.name}</span>
                <span className="cab-list__meta">
                  {(() => {
                    const area = row.area_microha === null ? noData() : haUnit(row.area_microha / 1e6);
                    return row.missing_rows ? t(m.areaNoValue, { area, missing: num(row.missing_rows), rows: num(row.rows) }) : area;
                  })()}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <ContourMap contour={contour} />
    </Stack>
  );
}

/* ------------------------------------------------------------ культуры */

function CropsTab({ contour }: { contour: Detail }) {
  const t = useT();
  const phaseOf = (phase: string) => (phase in PHASE_MSG ? t(PHASE_MSG[phase as keyof typeof PHASE_MSG]) : phase);
  const blocks = [...contour.crop_timeline].sort((a, b) => b.cycle_year - a.cycle_year);
  return (
    <div data-guide="crops">
      <Card size="lg" title={t(m.cropsTitle)} aside={t(m.yearsCount, { count: blocks.length })}>
        {blocks.length === 0 ? (
          <EmptyState compact title={t(m.cropsEmptyTitle)} reason={t(m.cropsEmptyReason)} />
        ) : (
          <Stack tight>
            {blocks.map((b) => (
              <div key={b.cycle_year}>
                <strong>{t(m.cropsYear, { year: b.cycle_year })}</strong>
                {b.is_demo ? <span className="cab-why"> · {t(m.cropsDemo)}</span> : null}
                <ul className="cab-list">
                  {b.rows.map((r) => (
                    <li className="cab-list__row" key={r.id}>
                      <span>
                        <Badge tone="outline">{cropLabel(r.crop_code).known ? cropLabel(r.crop_code).label : r.crop_name ?? r.crop_code ?? t(m.cropUnnamed)}</Badge> {phaseOf(r.phase)}
                        {r.part_label ? t(m.cropPart, { part: r.part_label }) : ''}
                        {r.is_current ? t(m.cropCurrent) : ''}
                      </span>
                      <span className="cab-list__meta">
                        {r.actual_from
                          ? t(m.cropMetaFact, { area: haUnit(r.area_microha / 1e6), window: windowShort(r.planned_from, r.planned_to), from: dateShort(r.actual_from) })
                          : t(m.cropMeta, { area: haUnit(r.area_microha / 1e6), window: windowShort(r.planned_from, r.planned_to) })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Stack>
        )}
      </Card>
    </div>
  );
}
